import React, { useEffect, useRef } from "react";
import { useCodexSignIn } from "@/shared/api/codex-oauth";
import { PrimaryButton, GhostButton } from "@/shared/components/settings-atoms";
import type { ProviderConnection } from "@/shared/types/models";

export default function CodexSignIn({ name, connectionId, canManage, onSaved, onCancel, onBusy }: {
  name: string; connectionId?: string; canManage: boolean;
  onSaved: (connection: ProviderConnection) => void; onCancel: () => void; onBusy: (busy: boolean) => void;
}) {
  const oauth = useCodexSignIn();
  const saved = useRef(false);
  const attempt = oauth.start.data;
  const busy = oauth.start.isPending || oauth.cancel.isPending;
  const error = oauth.start.error || oauth.cancel.error || oauth.poll.error;
  useEffect(() => { onBusy(busy || !!attempt); return () => onBusy(false); }, [busy, attempt, onBusy]);
  useEffect(() => {
    if (oauth.connection && !saved.current) { saved.current = true; onSaved(oauth.connection); }
  }, [oauth.connection, onSaved]);
  return <form className="space-y-3" onSubmit={async (event) => {
    event.preventDefault();
    if (!canManage || !name.trim() || busy || attempt) return;
    try { await oauth.start.mutateAsync({ name: name.trim(), connection_id: connectionId }); }
    catch { /* The fixed sign-in failure is shown below. */ }
  }}>
    <div className="space-y-2 rounded-[12px] p-3 bg-[var(--surface-sunk)] text-[12px] text-[var(--text-secondary)]">
      <p>Sign in with ChatGPT on OpenAI’s website. Enter the code shown below and approve access. This page connects automatically.</p>
      <p>Definable stores your credentials encrypted and refreshes them automatically. Your workspace’s agents share your subscription limits.</p>
      <p>Experimental access through Codex’s sign-in flow. The consent screen identifies Codex. Provider changes may interrupt this connection.</p>
    </div>
    {attempt && !oauth.expired && !oauth.stopped && <div className="space-y-3">
      <div><p className="text-[12px] text-[var(--text-tertiary)]">Enter this code on OpenAI’s sign-in page</p><p aria-label="Sign-in code" className="font-mono tabular-nums text-[19px] tracking-wide select-all">{attempt.user_code}</p></div>
      <a href={attempt.authorization_url} target="_blank" rel="noopener noreferrer" className="press inline-flex items-center h-8 px-3.5 rounded-[10px] bg-[var(--brand)] shadow-button text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--hairline-focus)]">Open OpenAI sign-in ↗</a>
      <p role="status" className="text-[12px] text-[var(--text-tertiary)]">Waiting for approval. Keep this page open. The code expires in {Math.round(attempt.expires_in / 60)} minutes.</p>
    </div>}
    {oauth.expired && <p role="alert" className="text-[var(--danger)]">This sign-in code expired. Start again for a new code.</p>}
    {error && !oauth.expired && <p role="alert" className="text-[var(--danger)]">{error.message}</p>}
    <div className="flex justify-end gap-2">
      <GhostButton type="button" disabled={busy} onClick={async () => {
        try { await oauth.reset(); onCancel(); } catch { /* A cancellation error remains visible. */ }
      }}>Cancel</GhostButton>
      {attempt ? <GhostButton type="button" disabled={busy} onClick={async () => {
        try { await oauth.reset(); } catch { /* A cancellation error remains visible. */ }
      }}>Start again</GhostButton> : <PrimaryButton type="submit" disabled={!canManage || !name.trim() || busy}>{busy ? "Connecting…" : "Continue with ChatGPT"}</PrimaryButton>}
    </div>
  </form>;
}
